import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { FaEdit, FaTrash, FaPlus, FaEye, FaFile, FaDownload } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Modal, Button, Spinner } from "react-bootstrap";
import * as XLSX from "xlsx";
import mammoth from "mammoth";
import { toast } from "react-toastify";

const DocumentDetails = ({ setDocumentsList, documentsList = [], documentType = [], userData }) => {

    const initialState = {
        type: null,
        IssueDate: null,
        documentNo: "",
        file: null,
        expiryDate: null,
        Remarks: "",
        verified: false,
        document_Path: "",
        url: "",
        fileName: "",
    };


    const [formData, setFormData] = useState(initialState);
    const [editIndex, setEditIndex] = useState(null);
    const fileInputRef = useRef(null);

    // FILE PREVIEW STATES

    const [showFileModal, setShowFileModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState("");
    const [previewType, setPreviewType] = useState("");
    const [previewUrl, setPreviewUrl] = useState("");
    const [excelData, setExcelData] = useState([]);
    const [excelSheets, setExcelSheets] = useState([]);
    const [excelWorkbook, setExcelWorkbook] = useState(null);
    const [selectedSheet, setSelectedSheet] = useState("");
    const [docxHtml, setDocxHtml] = useState("");

    // DATE HELPERS
    const parseDate = useCallback((value) => {
        if (!value) return null;

        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }

        // Backend uses this as an empty/sentinel date.
        if (typeof value === "string" && value.startsWith("1899-12-31")) {
            return null;
        }

        if (typeof value === "string") {
            const trimmed = value.trim();

            // Handles dd-MMM-yyyy, e.g. 28-Sep-2026.
            const match = trimmed.match(/^(\\d{1,2})-([A-Za-z]{3})-(\\d{4})$/);
            if (match) {
                const months = {
                    Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
                    Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
                };
                const month = months[match[2].substring(0, 3)];
                if (month !== undefined) {
                    const result = new Date(Number(match[3]), month, Number(match[1]));
                    return Number.isNaN(result.getTime()) ? null : result;
                }
            }

            // Avoid timezone shifts for API date-only values.
            const dateOnly = trimmed.match(/^(\\d{4})-(\\d{2})-(\\d{2})$/);
            if (dateOnly) {
                const result = new Date(
                    Number(dateOnly[1]),
                    Number(dateOnly[2]) - 1,
                    Number(dateOnly[3])
                );
                return Number.isNaN(result.getTime()) ? null : result;
            }
        }

        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }, []);

    const formatDate = useCallback((date) => {
        const parsedDate = parseDate(date);
        if (!parsedDate) return "";

        return parsedDate.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    }, [parseDate]);

    // RELEASE only browser-created blob URLs.
    const revokeObjectUrl = useCallback((url) => {
        if (typeof url === "string" && url.startsWith("blob:")) {
            try {
                URL.revokeObjectURL(url);
            } catch {
                // Ignore already-released object URLs.
            }
        }
    }, []);

    const handleCloseFileModal = () => {
        revokeObjectUrl(previewUrl);

        setPreviewUrl("");
        setSelectedFile(null);
        setPreviewType("");
        setPreviewError("");
        setExcelData([]);
        setExcelSheets([]);
        setExcelWorkbook(null);
        setSelectedSheet("");
        setDocxHtml("");
        setShowFileModal(false);
    };

    useEffect(() => {
        return () => {
            revokeObjectUrl(previewUrl);
            revokeObjectUrl(formData?.url);
        };
    }, [previewUrl, formData?.url, revokeObjectUrl]);

    // CHANGE EXCEL SHEET
    const handleExcelSheetChange = (sheetName) => {
        setSelectedSheet(sheetName);

        if (!excelWorkbook?.Sheets?.[sheetName]) {
            setExcelData([]);
            return;
        }

        setExcelData(
            XLSX.utils.sheet_to_json(
                excelWorkbook.Sheets[sheetName],
                {
                    header: 1,
                    defval: "",
                }
            )
        );
    };
    const handleInputChange = (e) => {
        const { name, value, files } = e.target;

        if (name === "file") {
            const selectedFile = files?.[0];
            if (!selectedFile) return;

            const maxSize = 10 * 1024 * 1024;
            if (selectedFile.size > maxSize) {
                toast.error("File size cannot exceed 10 MB.");
                e.target.value = "";
                return;
            }

            const localUrl = URL.createObjectURL(selectedFile);

            // Release the previous local preview, but never revoke a backend URL.
            revokeObjectUrl(formData?.url);

            setFormData((prev) => ({
                ...prev,
                file: selectedFile,
                fileName: selectedFile.name,
                url: localUrl,
                // A replacement file is a NEW upload; its backend path is
                // intentionally cleared. Parent save/upload will fill it.
                document_Path: "",
                filePath: "",
            }));

            return;
        }

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSelectChange = (selected) => {
        setFormData((prev) => ({
            ...prev,
            type: selected || null,
        }));
    };

    const handleDateChange = (name, date) => {
        setFormData((prev) => ({
            ...prev,
            [name]: date || null,
        }));
    };

    const resetForm = useCallback(() => {
        revokeObjectUrl(formData?.url);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }

        setFormData({ ...initialState });
        setEditIndex(null);
    }, [formData?.url, revokeObjectUrl]);

    const handleAddOrUpdate = () => {
        if (!formData?.type?.label) {
            toast.error("Please select document type.");
            return;
        }

        // New rows must have a file. Existing rows may keep their backend file.
        if (editIndex === null && !(formData.file instanceof File) && !formData.document_Path && !formData.url) {
            toast.error("Please select a file.");
            return;
        }

        const rowToSave = {
            ...formData,
            IssueDate: parseDate(formData.IssueDate),
            expiryDate: parseDate(formData.expiryDate),
            fileName: formData.file?.name || formData.fileName || "",
        };

        if (editIndex !== null) {
            setDocumentsList((prev = []) => {
                const updated = [...prev];
                const oldRow = normalizeDocument(prev[editIndex]);

                updated[editIndex] = {
                    ...oldRow,
                    ...rowToSave,

                    // If a replacement file was selected, keep its local URL
                    // until parent save uploads it. Otherwise preserve backend URL.
                    url: rowToSave.file instanceof File
                        ? rowToSave.url
                        : oldRow.url,

                    // If a replacement file was selected, parent save must
                    // upload it and generate the new backend path.
                    document_Path: rowToSave.file instanceof File
                        ? ""
                        : oldRow.document_Path,

                    filePath: rowToSave.file instanceof File
                        ? ""
                        : oldRow.filePath,

                    fileName: rowToSave.file?.name || oldRow.fileName || "",
                };

                return updated;
            });

            toast.success("Document updated successfully.");
        } else {
            setDocumentsList((prev = []) => [...prev, rowToSave]);
            toast.success("Document added successfully.");
        }

        resetForm();
    };

    const handleEdit = (index) => {
        const document = normalizeDocument(documentsList[index]);

        setFormData({
            ...document,
            file: null,
            // Existing backend URL is only for preview. Selecting a new file
            // later will replace it with a blob URL and clear document_Path.
            url: document.url || "",
            document_Path: document.document_Path || "",
            filePath: document.filePath || document.document_Path || "",
            fileName: document.fileName || getFileName(document),
            IssueDate: parseDate(document.IssueDate),
            expiryDate: parseDate(document.expiryDate),
        });

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }

        setEditIndex(index);
    };

    const handleDelete = (index) => {
        const confirmed = window.confirm("Are you sure you want to remove this document?");
        if (!confirmed) return;

        setDocumentsList((prev = []) => prev.filter((_, i) => i !== index));

        if (editIndex === index) {
            resetForm();
        } else if (editIndex !== null && index < editIndex) {
            setEditIndex((current) => current - 1);
        }

        toast.success("Document removed.");
    };

    // DOCUMENT TYPE OPTIONS
    const documentTypeOptions = useMemo(() => {
        return (
            documentType?.map((item) => ({
                value: item?.value,
                label: item?.text,
            })) || []
        );
    }, [documentType]);
    // FILE EXTENSION
    const getFileExtension = (fileName = '', url = '') => {
        const source = fileName || url || '';

        const cleanSource = String(source)
            .split('?')[0]
            .split('#')[0];

        const parts = cleanSource.split('.');

        return parts.length > 1
            ? parts.pop().toLowerCase()
            : '';
    };
    // GET FILE NAME
    const getFileName = (document) => {
        if (!document) {
            return "Uploaded File";
        }

        // NEW FILE
        if (document.file instanceof File) {
            return document.file.name;
        }

        // Existing mapped file name
        if (document.fileName) {
            return document.fileName;
        }

        // Document path from backend
        const documentPath =
            document.Document_Path ||
            document.document_Path ||
            document.filePath ||
            "";

        if (documentPath) {
            try {
                const cleanPath = String(documentPath)
                    .split("?")[0]
                    .split("#")[0];

                const parts = cleanPath.split("/");

                return decodeURIComponent(
                    parts[parts.length - 1]
                );
            } catch {
                return "Uploaded File";
            }
        }

        // Existing URL
        const url =
            document.url ||
            document.Url ||
            "";

        if (url) {
            try {
                const cleanUrl = String(url)
                    .split("?")[0]
                    .split("#")[0];

                const parts = cleanUrl.split("/");

                return decodeURIComponent(
                    parts[parts.length - 1]
                );
            } catch {
                return "Uploaded File";
            }
        }

        return "Uploaded File";
    };
    // BUILD THE CANONICAL EXISTING-BACKEND FILE URL.
    //
    // API response:
    // Url            = https://lease.api.apps.rabahrain.com/Resources/
    // Document_Path  = IMG_....JPG
    //
    // Required:
    // https://lease.api.apps.rabahrain.com/Resources/ePMS9100/44502/IMG_....JPG
    const getDocumentUrl = useCallback((item) => {
        if (!item) return "";
        debugger
        // Never treat a local File/blob as a backend document.
        if (item.file instanceof File) return "";

        const db = localStorage.getItem("DataBase")?.trim() || "";
        const customerNo = String(userData?.customerNo ?? "").trim();

        const documentPath = String(
            item.Document_Path ||
            item.document_Path ||
            item.filePath ||
            ""
        ).trim();

        const rawUrl = String(item.imgurl || item.imgurl || "").trim();

        // If a document path exists, rebuild the URL from the API base.
        // This deliberately ignores an older/incorrect `url` such as:
        // /Resources/IMG_123.JPG
        if (rawUrl && documentPath) {
            const cleanBaseUrl = rawUrl.replace(/\/+$/, "");
            debugger
            // Already canonical: don't append db/customer twice.
            const canonicalMarker =
                db && customerNo
                    ? `/${encodeURIComponent(db)}/${encodeURIComponent(customerNo)}/`
                    : "";

            if (canonicalMarker && cleanBaseUrl.includes(canonicalMarker)) {
                return `${cleanBaseUrl}/${documentPath
                    .replace(/^\/+/, "")
                    .split("/")
                    .map((part) => encodeURIComponent(part))
                    .join("/")}`;
            }

            if (!db || !customerNo) {
                // We cannot safely construct the expected resource path.
                // Fall back to the API-provided URL only if it already looks
                // like a complete resource URL.
                if (/\/Resources\/[^/]+\/[^/]+\//i.test(cleanBaseUrl)) {
                    return `${cleanBaseUrl}/${documentPath
                        .replace(/^\/+/, "")
                        .split("/")
                        .map((part) => encodeURIComponent(part))
                        .join("/")}`;
                }
                return "";
            }
            debugger
            return `${cleanBaseUrl}/${encodeURIComponent(db)}/${encodeURIComponent(customerNo)}/${documentPath
                .replace(/^\/+/, "")
                .split("/")
                .map((part) => encodeURIComponent(part))
                .join("/")}`;
        }

        // If no path is available, a complete backend URL can still be used.
        if (rawUrl && /^https?:\/\//i.test(rawUrl)) {
            return rawUrl;
        }

        return "";
    }, [userData?.customerNo]);

    const normalizeDocument = useCallback((item) => {
        debugger
        if (!item) {
            return {
                ...initialState,
                url: "",
                filePath: "",
                document_Path: "",
                fileName: "",
            };
        }

        const filePath = String(
            item.filePath ||
            item.document_Path ||
            item.Document_Path ||
            ""
        ).trim();

        const localFile = item.file instanceof File ? item.file : null;

        // For existing backend records, always generate the canonical URL
        // from Url + DataBase + customerNo + Document_Path.
        const fileUrl = localFile ? "" : getDocumentUrl({
            ...item,
            filePath,
            document_Path: filePath,
        });
        debugger
        let type = item.type || null;
        if (!type && item.Document_Type) {
            type = {
                value: item.Document_Type,
                label: item.Document_Type,
            };
        }

        const issueDate = parseDate(item.IssueDate ?? item.Doc_IssueDate);
        const expiryDate = parseDate(item.expiryDate ?? item.Doc_ExpiryDate);

        return {
            ...item,
            type,

            documentNo: item.documentNo ?? item.Document_No ?? "",
            Remarks: item.Remarks ?? item.Document_Desc ?? "",
            IssueDate: issueDate,
            expiryDate: expiryDate,

            verified: item.verified ?? item.Verified ?? false,

            file: localFile,

            filePath,
            document_Path: filePath,

            url: localFile ? (item.url || "") : fileUrl,

            fileName:
                item.fileName ||
                localFile?.name ||
                (filePath ? getFileName({ document_Path: filePath }) : ""),
        };
    }, [getDocumentUrl, parseDate]);

    // NORMALIZED DOCUMENTS
    const normalizedDocuments = useMemo(() => {
        debugger
        return (documentsList || []).map(normalizeDocument);
    }, [documentsList, normalizeDocument]);
    // FILE TYPE
    const getFileType = (fileName = '', url = '') => {
        const extension =
            getFileExtension(fileName, url);

        if (
            [
                "jpg",
                "jpeg",
                "png",
                "gif",
                "webp",
                "bmp",
                "svg",
            ].includes(extension)
        ) {
            return "image";
        }

        if (extension === "pdf") {
            return "pdf";
        }

        if (
            [
                "doc",
                "docx",
            ].includes(extension)
        ) {
            return "word";
        }

        if (
            [
                "xls",
                "xlsx",
                "csv",
            ].includes(extension)
        ) {
            return "excel";
        }

        if (
            [
                "txt",
                "log",
            ].includes(extension)
        ) {
            return "text";
        }

        return "other";
    };
    // GET FILE BLOB
    const getFileBlob = async (document) => {
        const row =
            normalizeDocument(document);

        /*
         * =====================================================
         * NEW FILE
         * =====================================================
         *
         * The file has not been uploaded yet.
         *
         * Example:
         * row.file = File
         *
         * So we directly return the File.
         */
        if (row.file instanceof File) {
            return {
                blob: row.file,
                fileName: row.file.name,
            };
        }

        /*
         * =====================================================
         * EXISTING BACKEND FILE
         * =====================================================
         */

        const fileUrl =
            row.url ||
            getDocumentUrl(row);

        if (!fileUrl) {
            throw new Error(
                "File URL is not available."
            );
        }

        const token =
            localStorage.getItem("Token");

        const db =
            localStorage.getItem("DataBase");

        const response = await fetch(
            fileUrl,
            {
                method: "GET",

                headers: {
                    accept: "*/*",

                    apikey:
                        "V+kBloEwqqVho1PINmQI0VJQwSBX2l/e",

                    Authorization:
                        `Bearer ${token}`,

                    db: db || "",
                },
            }
        );

        if (!response.ok) {
            throw new Error(
                `Unable to load file (${response.status})`
            );
        }

        const blob =
            await response.blob();

        return {
            blob,
            fileName: getFileName(row),
        };
    };
    // VIEW FILE
    const handleView = async (document) => {
        try {
            debugger
            const row = normalizeDocument(document);

            /*
             * New file:
             * row.file exists.
             *
             * Existing file:
             * row.url exists.
             */
            if (
                !(row.file instanceof File) &&
                !row.url
            ) {
                toast.warning(
                    "File is not available."
                );

                return;
            }

            /*
             * Reset preview state
             */
            setPreviewError("");
            setPreviewUrl("");
            setExcelData([]);
            setExcelSheets([]);
            setExcelWorkbook(null);
            setSelectedSheet("");
            setDocxHtml("");

            /*
             * Store selected document
             */
            setSelectedFile({ ...row, fileName: getFileName(row), });

            setShowFileModal(true);
            setPreviewLoading(true);

            /*
             * Determine file type
             */
            const fileName =
                getFileName(row);

            const type =
                getFileType(
                    fileName,
                    row.url
                );

            setPreviewType(type);

            /*
             * Get file:
             *
             * NEW:
             * directly gets File object
             *
             * EXISTING:
             * fetches backend URL
             */
            const {
                blob,
            } = await getFileBlob(row);

            /*
             * Create temporary browser URL
             */
            const objectUrl =
                URL.createObjectURL(blob);

            setPreviewUrl(objectUrl);


            // =================================================
            // IMAGE
            // =================================================

            if (type === "image") {
                return;
            }


            // =================================================
            // PDF
            // =================================================

            if (type === "pdf") {
                return;
            }


            // =================================================
            // EXCEL
            // =================================================

            if (type === "excel") {
                const arrayBuffer =
                    await blob.arrayBuffer();

                const workbook =
                    XLSX.read(
                        arrayBuffer,
                        {
                            type: "array",
                        }
                    );

                setExcelWorkbook(workbook);

                const sheetNames =
                    workbook.SheetNames || [];

                setExcelSheets(
                    sheetNames
                );

                const firstSheet =
                    sheetNames[0];

                if (firstSheet) {
                    setSelectedSheet(
                        firstSheet
                    );

                    setExcelData(
                        XLSX.utils.sheet_to_json(
                            workbook.Sheets[firstSheet],
                            {
                                header: 1,
                                defval: "",
                            }
                        )
                    );
                }

                return;
            }


            // =================================================
            // WORD / DOCX
            // =================================================

            if (type === "word") {
                const arrayBuffer =
                    await blob.arrayBuffer();

                /*
                 * mammoth supports DOCX.
                 */
                if (fileName.toLowerCase().endsWith(".docx")) {
                    const result = await mammoth.convertToHtml({
                        arrayBuffer,
                    });

                    setDocxHtml(result.value || "");
                    return;
                }

                // Mammoth does not reliably render legacy .doc files.
                setDocxHtml("");
                setPreviewType("doc");
                return;
            }


            // =================================================
            // TEXT
            // =================================================

            if (type === "text") {
                const text =
                    await blob.text();

                setDocxHtml(text);

                return;
            }

        } catch (error) {
            console.error(
                "File preview error:",
                error
            );

            setPreviewError(
                error?.message ||
                "Unable to load file."
            );
        } finally {
            setPreviewLoading(false);
        }
    };
    return (

        <div className="cost-center-section my-3">

            <div className="border-box2 p-3" style={{ borderRadius: "8px", }}            >

                {/* YOUR FORM */}

                <div className="form-group grid-contain">

                    <div className="d-flex gap-3 align-items-center">

                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="documentVerified"
                            style={{
                                width: "20px",
                                height: "20px",
                            }}
                            checked={
                                formData.verified
                            }
                            onChange={(e) =>
                                setFormData(
                                    (prev) => ({
                                        ...prev,
                                        verified:
                                            e.target
                                                .checked,
                                    })
                                )
                            }
                        />

                        <label
                            className="form-check-label"
                            htmlFor="documentVerified"
                        >
                            Document Verified
                        </label>

                    </div>


                    <div></div>
                    <div></div>


                    {/* Document Type */}

                    <div className="d-flex">

                        <label className="form-label pt-1 start">
                            Document Type
                        </label>

                        <Select
                            className="p-0 w-100"
                            menuPortalTarget={
                                document.body
                            }
                            options={
                                documentTypeOptions
                            }
                            value={
                                formData.type
                            }
                            onChange={
                                handleSelectChange
                            }
                            isClearable
                            placeholder="Select Document Type"
                            styles={{
                                menuPortal:
                                    (base) => ({
                                        ...base,
                                        zIndex:
                                            99999,
                                    }),
                            }}
                        />

                    </div>


                    {/* Issue Date */}

                    <div className="d-flex">

                        <label className="form-label pt-1 start">
                            Issue Date
                        </label>

                        <DatePicker
                            selected={
                                formData.IssueDate
                            }
                            onChange={(date) =>
                                handleDateChange(
                                    "IssueDate",
                                    date
                                )
                            }
                            dateFormat="dd-MMM-yyyy"
                            className="p-2 form-control w-100"
                            isClearable
                        />

                    </div>


                    {/* Document No */}

                    <div className="d-flex">

                        <label className="form-label pt-1 start">
                            Document No
                        </label>

                        <input
                            type="text"
                            className="p-2 form-control w-75"
                            name="documentNo"
                            value={
                                formData.documentNo
                            }
                            onChange={
                                handleInputChange
                            }
                        />

                    </div>


                    {/* File */}

                    <div className="d-flex">

                        <label className="form-label pt-1 start">
                            File
                        </label>

                        <div className="w-75">

                            <input
                                ref={fileInputRef}
                                type="file"
                                className="p-2 form-control"
                                name="file"
                                onChange={
                                    handleInputChange
                                }
                                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.log"
                            />

                            {formData.fileName && (

                                <small className="text-muted">
                                    Current file:{" "}
                                    <strong>
                                        {
                                            formData.fileName
                                        }
                                    </strong>
                                </small>

                            )}

                        </div>

                    </div>


                    {/* Expiry Date */}

                    <div className="d-flex">

                        <label className="form-label pt-1 start">
                            Expiry Date
                        </label>

                        <DatePicker
                            selected={
                                formData.expiryDate
                            }
                            onChange={(date) =>
                                handleDateChange(
                                    "expiryDate",
                                    date
                                )
                            }
                            dateFormat="dd-MMM-yyyy"
                            className="p-2 form-control w-100"
                            isClearable
                        />

                    </div>


                    {/* Remarks */}

                    <div className="d-flex">

                        <label className="form-label pt-1 start">
                            Remarks
                        </label>

                        <input
                            type="text"
                            className="p-2 form-control w-75"
                            name="Remarks"
                            value={
                                formData.Remarks
                            }
                            onChange={
                                handleInputChange
                            }
                        />

                    </div>

                </div>


                {/* =====================================================
                    ADD BUTTON
                ===================================================== */}

                <div className="my-4 d-flex justify-content-end">

                    <div
                        className="mainbtnSecondary flex-end gap-2"
                        style={{
                            cursor: "pointer",
                            minWidth: "150px",
                        }}
                        onClick={
                            handleAddOrUpdate
                        }
                    >

                        {editIndex !== null ? (
                            <FaEdit
                                color="#fff"
                                className="center mt-1 mx-2"
                            />
                        ) : (
                            <FaPlus
                                color="#fff"
                                className="center mt-1 mx-2"
                            />
                        )}

                        <div className="center">

                            {editIndex !== null
                                ? "Update File"
                                : "Add File"}

                        </div>

                    </div>

                </div>


                {/* =====================================================
                    TABLE
                ===================================================== */}

                <div
                    className="table-container"
                    style={{
                        overflowX: "auto",
                        height: "400px",
                    }}
                >

                    <table className="table table-bordered gridshad mb-0">

                        <thead className="dynoTable">

                            <tr>

                                <th>
                                    Action
                                </th>

                                <th>
                                    Document Type
                                </th>

                                <th>
                                    Document No
                                </th>

                                <th>
                                    File
                                </th>

                                <th>
                                    Expiry Date
                                </th>

                                <th>
                                    Issue Date
                                </th>

                                <th>
                                    Remarks
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {normalizedDocuments.length === 0 ? (

                                <tr>

                                    <td
                                        colSpan="7"
                                        className="text-center py-4"
                                    >
                                        No documents added.
                                    </td>

                                </tr>

                            ) : (

                                normalizedDocuments.map(
                                    (data, index) => (

                                        <tr key={index}>

                                            {/* ACTION */}

                                            <td>

                                                <div
                                                    className="d-flex justify-content-center align-items-center gap-3"
                                                >

                                                    {/* VIEW */}

                                                    <button
                                                        type="button"
                                                        className="btn btn-sm p-0 border-0 bg-transparent"
                                                        title="View File"
                                                        onClick={() =>
                                                            handleView(
                                                                data
                                                            )
                                                        }
                                                        disabled={
                                                            !(
                                                                data.file ||
                                                                data.url
                                                            )
                                                        }
                                                    >

                                                        <FaEye
                                                            size={18}
                                                            style={{
                                                                cursor:
                                                                    data.file ||
                                                                        data.url
                                                                        ? "pointer"
                                                                        : "not-allowed",

                                                                opacity:
                                                                    data.file ||
                                                                        data.url
                                                                        ? 1
                                                                        : 0.35,
                                                            }}
                                                        />

                                                    </button>


                                                    {/* EDIT */}

                                                    <button
                                                        type="button"
                                                        className="btn btn-sm p-0 border-0 bg-transparent"
                                                        title="Edit"
                                                        onClick={() =>
                                                            handleEdit(
                                                                index
                                                            )
                                                        }
                                                    >

                                                        <FaEdit
                                                            size={18}
                                                        />

                                                    </button>


                                                    {/* REMOVE */}

                                                    <button
                                                        type="button"
                                                        className="btn btn-sm p-0 border-0 bg-transparent"
                                                        title="Remove"
                                                        onClick={() =>
                                                            handleDelete(
                                                                index
                                                            )
                                                        }
                                                    >

                                                        <FaTrash
                                                            size={18}
                                                            color="red"
                                                        />

                                                    </button>

                                                </div>

                                            </td>


                                            {/* TYPE */}

                                            <td>
                                                {data.type?.label ||
                                                    data.Document_Type ||
                                                    "-"}
                                            </td>


                                            {/* DOCUMENT NO */}

                                            <td>
                                                {data.documentNo ||
                                                    data.Document_No ||
                                                    "-"}
                                            </td>


                                            {/* FILE */}

                                            <td>

                                                <div className="d-flex align-items-center gap-2">

                                                    <FaFile />

                                                    <span>
                                                        {
                                                            getFileName(
                                                                data
                                                            )
                                                        }
                                                    </span>

                                                </div>

                                            </td>


                                            {/* EXPIRY */}

                                            <td>
                                                {
                                                    formatDate(
                                                        data.expiryDate ||
                                                        data.Doc_ExpiryDate
                                                    ) || "-"
                                                }
                                            </td>


                                            {/* ISSUE */}

                                            <td>
                                                {
                                                    formatDate(
                                                        data.IssueDate ||
                                                        data.Doc_IssueDate
                                                    ) || "-"
                                                }
                                            </td>


                                            {/* REMARKS */}

                                            <td>
                                                {data.Remarks ||
                                                    data.Document_Desc ||
                                                    "-"}
                                            </td>

                                        </tr>

                                    )
                                )
                            )}

                        </tbody>

                    </table>

                </div>

            </div>


            {/* =========================================================
                BOOTSTRAP MODAL
            ========================================================= */}

            <Modal
                show={showFileModal}
                onHide={
                    handleCloseFileModal
                }
                size="xl"
                centered
                backdrop="static"
                keyboard={true}
            >

                <Modal.Header
                    closeButton
                    style={{
                        backgroundColor:
                            "#6E5199",
                        color: "#fff",
                    }}
                >

                    <Modal.Title
                        className="d-flex align-items-center gap-2"
                        style={{
                            fontSize: "17px",
                        }}
                    >

                        <FaFile />

                        {selectedFile?.fileName ||
                            "File Preview"}

                    </Modal.Title>

                </Modal.Header>


                <Modal.Body
                    style={{
                        height: "75vh",
                        padding: "0",
                        backgroundColor:
                            "#f5f5f5",
                        overflow: "hidden",
                    }}
                >

                    {/* =================================================
                        LOADING
                    ================================================= */}

                    {previewLoading && (

                        <div
                            className="h-100 d-flex flex-column justify-content-center align-items-center"
                        >

                            <Spinner
                                animation="border"
                                style={{
                                    color:
                                        "#6E5199",
                                }}
                            />

                            <div className="mt-3">
                                Loading file...
                            </div>

                        </div>

                    )}


                    {/* =================================================
                        ERROR
                    ================================================= */}

                    {!previewLoading &&
                        previewError && (

                            <div
                                className="h-100 d-flex flex-column justify-content-center align-items-center"
                            >

                                <FaFile
                                    size={60}
                                    color="#6E5199"
                                />

                                <h5 className="mt-3">
                                    Unable to preview file
                                </h5>

                                <p className="text-danger text-center px-4">
                                    {
                                        previewError
                                    }
                                </p>

                                {previewUrl && (

                                    <a
                                        href={
                                            previewUrl
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-primary"
                                    >
                                        Open File
                                    </a>

                                )}

                            </div>

                        )}


                    {/* =================================================
                        IMAGE
                    ================================================= */}

                    {!previewLoading &&
                        !previewError &&
                        previewType === "image" && (

                            <div
                                className="w-100 h-100 d-flex justify-content-center align-items-center p-3"
                                style={{
                                    overflow:
                                        "auto",
                                }}
                            >

                                <img
                                    src={
                                        previewUrl
                                    }
                                    alt={
                                        selectedFile?.fileName
                                    }
                                    style={{
                                        maxWidth:
                                            "100%",
                                        maxHeight:
                                            "100%",
                                        objectFit:
                                            "contain",
                                    }}
                                />

                            </div>

                        )}


                    {/* =================================================
                        PDF
                    ================================================= */}

                    {!previewLoading &&
                        !previewError &&
                        previewType === "pdf" && (

                            <iframe
                                src={
                                    previewUrl
                                }
                                title={
                                    selectedFile?.fileName
                                }
                                style={{
                                    width:
                                        "100%",
                                    height:
                                        "100%",
                                    border:
                                        "none",
                                    backgroundColor:
                                        "#fff",
                                }}
                            />

                        )}


                    {/* =================================================
                        EXCEL
                    ================================================= */}

                    {!previewLoading &&
                        !previewError &&
                        previewType === "excel" && (

                            <div
                                style={{
                                    height:
                                        "100%",
                                    overflow:
                                        "auto",
                                    backgroundColor:
                                        "#fff",
                                    padding:
                                        "15px",
                                }}
                            >

                                {/* SHEETS */}

                                {excelSheets.length >
                                    1 && (

                                        <div className="mb-3">

                                            <label className="fw-bold">
                                                Sheet
                                            </label>

                                            <select
                                                className="form-select"
                                                value={
                                                    selectedSheet
                                                }
                                                onChange={(e) =>
                                                    handleExcelSheetChange(
                                                        e.target
                                                            .value
                                                    )
                                                }
                                            >

                                                {excelSheets.map(
                                                    (
                                                        sheet
                                                    ) => (

                                                        <option
                                                            key={
                                                                sheet
                                                            }
                                                            value={
                                                                sheet
                                                            }
                                                        >
                                                            {
                                                                sheet
                                                            }
                                                        </option>

                                                    )
                                                )}

                                            </select>

                                        </div>

                                    )}


                                {excelData.length >
                                    0 ? (

                                    <div
                                        style={{
                                            overflow:
                                                "auto",
                                        }}
                                    >

                                        <table
                                            className="table table-bordered table-striped table-hover"
                                            style={{
                                                fontSize:
                                                    "13px",
                                                whiteSpace:
                                                    "nowrap",
                                            }}
                                        >

                                            <tbody>

                                                {excelData.map(
                                                    (
                                                        row,
                                                        rowIndex
                                                    ) => (

                                                        <tr
                                                            key={
                                                                rowIndex
                                                            }
                                                        >

                                                            {row.map(
                                                                (
                                                                    cell,
                                                                    cellIndex
                                                                ) => (

                                                                    <td
                                                                        key={
                                                                            cellIndex
                                                                        }
                                                                    >
                                                                        {
                                                                            cell
                                                                        }
                                                                    </td>

                                                                )
                                                            )}

                                                        </tr>

                                                    )
                                                )}

                                            </tbody>

                                        </table>

                                    </div>

                                ) : (

                                    <div className="text-center text-muted mt-5">
                                        Excel file is empty.
                                    </div>

                                )}

                            </div>

                        )}


                    {/* =================================================
                        DOCX
                    ================================================= */}

                    {!previewLoading &&
                        !previewError &&
                        previewType === "word" && (

                            <div
                                style={{
                                    height:
                                        "100%",
                                    overflow:
                                        "auto",
                                    backgroundColor:
                                        "#fff",
                                    padding:
                                        "40px",
                                }}
                            >

                                <div
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            docxHtml,
                                    }}
                                />

                            </div>

                        )}


                    {/* =================================================
                        TEXT
                    ================================================= */}

                    {!previewLoading &&
                        !previewError &&
                        previewType === "text" && (

                            <div
                                style={{
                                    height: "100%",
                                    overflow: "auto",
                                    backgroundColor: "#fff",
                                    padding: "30px",
                                    whiteSpace: "pre-wrap",
                                    fontFamily: "monospace",
                                }}
                            >
                                {docxHtml}
                            </div>

                        )}


                    {/* =================================================
                        DOC / PPT / UNKNOWN
                    ================================================= */}

                    {!previewLoading &&
                        !previewError &&
                        [
                            "doc",
                            "powerpoint",
                            "other",
                        ].includes(
                            previewType
                        ) && (

                            <div
                                className="h-100 d-flex flex-column justify-content-center align-items-center"
                                style={{
                                    backgroundColor:
                                        "#fff",
                                }}
                            >

                                <FaFile
                                    size={70}
                                    color="#6E5199"
                                />

                                <h5 className="mt-3">
                                    Preview is not available
                                    for this file type.
                                </h5>

                                <p className="text-muted">
                                    {
                                        selectedFile?.fileName
                                    }
                                </p>

                                {previewUrl && (

                                    <a
                                        href={
                                            previewUrl
                                        }
                                        download={
                                            selectedFile?.fileName
                                        }
                                        className="btn btn-primary"
                                    >

                                        <FaDownload className="me-2" />

                                        Download File

                                    </a>

                                )}

                            </div>

                        )}

                </Modal.Body>


                {/* =================================================
                    FOOTER
                ================================================= */}

                <Modal.Footer>

                    {previewUrl && (

                        <a
                            href={
                                previewUrl
                            }
                            download={
                                selectedFile?.fileName
                            }
                            className="btn btn-outline-primary"
                        >

                            <FaDownload className="me-2" />

                            Download

                        </a>

                    )}

                    <Button
                        variant="secondary"
                        onClick={
                            handleCloseFileModal
                        }
                    >
                        Close
                    </Button>

                </Modal.Footer>

            </Modal>

        </div>
    );
};


export default DocumentDetails;